import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function CreateUserDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'customer',
    rbac_role_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [rbacRoles, setRbacRoles] = useState([]);

  useEffect(() => {
    if (open && formData.role === 'admin') {
      fetchRbacRoles();
    }
  }, [open, formData.role]);

  const fetchRbacRoles = async () => {
    try {
      const res = await api.get('/admin/rbac/roles');
      setRbacRoles(res.data.roles || []);
    } catch (err) {
      console.error('Failed to load RBAC roles');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Email, password, and name are required');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams(formData);
      const res = await api.post(`/admin/users/create?${params}`);
      
      // If admin role and RBAC role selected, assign it
      if (formData.role === 'admin' && formData.rbac_role_id) {
        await api.post(`/admin/users/${res.data.user.id}/assign-role?role_id=${formData.rbac_role_id}`);
      }
      
      toast.success(`User created successfully!`);
      setFormData({ email: '', password: '', name: '', phone: '', role: 'customer', rbac_role_id: '' });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="Min 8 characters"
              minLength={8}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
              placeholder="+1-555-0100"
            />
          </div>

          <div className="space-y-2">
            <Label>Role *</Label>
            <Select value={formData.role} onValueChange={value => setFormData({...formData, role: value, rbac_role_id: ''})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Note: All users are auto-verified</p>
          </div>

          {formData.role === 'admin' && (
            <div className="space-y-2">
              <Label>RBAC Role (Optional)</Label>
              <Select value={formData.rbac_role_id} onValueChange={value => setFormData({...formData, rbac_role_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Assign a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No specific role</SelectItem>
                  {rbacRoles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name} - {role.permissions?.length || 0} permissions
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Assign RBAC role for granular permission control
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
